import { Controller } from "@hotwired/stimulus";
import SignaturePad from "signature_pad";

let duration = 0;

export default class extends Controller {
  static targets = ["canvas", "placeholder"];

  connect() {
    this.initialize();
    duration = 0;
  }

  show() {
    if (!this.sigPad || this.sigPad.isEmpty()) {
      this.initialize();
    }
  }

  initialize() {
    if (!this.isVisible(this.canvasTarget)) return;

    this.setUpSignaturePad();
    this.resizeCanvas();
    this.addTimeListeners();
    this.addListeners();
  }

  setUpSignaturePad() {
    const brand = this.data.get("brandValue") || "bare";
    const penColor = (() => {
      switch (brand) {
        case "bare":
          return "#035e61";
        case "light":
          return "#003164";
        case "goodbyegoodboy":
          return "#7372a0";
      }
    })();

    const penConfig = {
      minWidth: 1,
      maxWidth: 3,
      penColor: penColor,
    };

    if (!this.sigPad) {
      this.sigPad = new SignaturePad(this.canvasTarget, penConfig);
    }
  }

  resizeCanvas() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const fixedHeight = 250;

    this.canvasTarget.width = this.canvasTarget.offsetWidth * ratio;
    this.canvasTarget.height = fixedHeight * ratio;
    this.canvasTarget.style.height = `${fixedHeight}px`;

    const context = this.canvasTarget.getContext("2d");
    context.scale(ratio, ratio);
    this.sigPad.clear();

    this.showPlaceholder();
  }

  addListeners() {
    this.canvasTarget.addEventListener("pointerdown", this.hidePlaceholder.bind(this));
    window.addEventListener("resize", this.resizeCanvas.bind(this));
  }

  addTimeListeners() {
    let startTime, endTime;
    this.canvasTarget.addEventListener('pointerdown', () => startTime = new Date());
    this.canvasTarget.addEventListener('pointerup', () => {
      endTime = new Date();
      duration += (endTime - startTime) / 1000;
    });
  }

  clearSignature(event) {
    event.preventDefault();
    this.sigPad?.clear();
    this.showPlaceholder();
    duration = 0;
    this.element.dispatchEvent(new CustomEvent("signature_pad:cleared", { bubbles: true }));
  }

  hidePlaceholder() {
    this.placeholderTarget.classList.add("hidden");
    this.fireSignedEvent();
  }

  showPlaceholder() {
    this.placeholderTarget.classList.remove("hidden");
    this.fireUnSignedEvent();
  }

  getSignatureData() {
    try {
      const croppedCanvas = this.cropSignature(this.canvasTarget);
      const img = croppedCanvas.toDataURL();
      return new Promise((resolve, reject) => {
        if (this.isSigned() && duration >= 0.5 && img.length >= 5000 && croppedCanvas.width >= 150 && croppedCanvas.height >= 75) {
          croppedCanvas.toBlob((blob) => {
            resolve(blob);
          }, "image/png");
        } else {
          reject(new Error("The signature is invalid. Please try making it a little bigger."));
          alert("The signature is invalid. Please try making it a little bigger.");
          location.reload();
        }
      });
    } catch (error) {
      alert("Cannot save the signature. Please contact us for support");
      location.reload();
    }
  }

  isSigned() {
    const sigData = this.sigPad.toData();
    return !this.sigPad.isEmpty() && sigData.length >= 1;
  }

  isVisible(element) {
    return !!element && element.offsetWidth > 0 && element.offsetHeight > 0;
  }

  fireSignedEvent() {
    this.element.dispatchEvent(new CustomEvent("signature_pad:signed", { bubbles: true }));
  }

  fireUnSignedEvent() {
    this.element.dispatchEvent(new CustomEvent("signature_pad:unsigned", { bubbles: true }));
  }

  cropSignature(canvas) {
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let top = null, bottom = null, left = null, right = null;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          if (top === null) top = y;
          bottom = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
        }
      }
    }

    if (top === null) {
      // No signature
      return canvas;
    }

    const croppedWidth = right - left + 1;
    const croppedHeight = bottom - top + 1;

    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;
    const croppedCtx = croppedCanvas.getContext("2d");

    croppedCtx.putImageData(
      ctx.getImageData(left, top, croppedWidth, croppedHeight),
      0,
      0
    );

    return croppedCanvas;
  }
}
